/**
 * WI-B-03 — the auth screens (A1, A2, A4, A5, A6) + sign-out modal, ported 1:1
 * from design-intake/auth-b/auth-screens-reference.html. A3 is WorkOS-hosted and
 * deliberately NOT built.
 *
 * Pure presentational Preact — no auth logic lives here. The state machine
 * (workos-callback.ts) decides WHICH screen; these render it. Literals from the
 * reference are replaced with the real --mark-* tokens where they exist.
 *
 * Reusable pieces (per the reference note): the amber Sign-in bar, the spinner
 * ring (A2/A4), the error block, the stale banner (A7, in session-banner.tsx).
 */

import type { JSX } from "preact";
import type { AuthErrorKind } from "../auth/workos-callback";
import { PillButton } from "./PillButton";

const BG = "var(--mark-bg-base)";
const SURFACE = "var(--mark-bg-surface)";
const AMBER = "var(--mark-accent-amber)";
const INK_ON_AMBER = "var(--mark-ink-on-amber)";
const WARN_TINT = "var(--mark-bg-warning-tint)";
const TEXT = "var(--mark-text-primary)";
const MUTED = "var(--mark-text-muted)";
const FAINT = "var(--mark-text-faint)";
const BORDER = "var(--mark-border-default)";
const CRITICAL = "var(--mark-status-critical)";
const BTN_DISABLED = "var(--mark-bg-button-disabled)";

const rootStyle: JSX.CSSProperties = {
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  padding: "48px 32px 40px",
  background: BG,
};

/** The BuildEngine logo — the same artwork as the app icon (public/icons/icon.svg). */
function BrandLogo() {
  return (
    <img src="/icons/icon.svg" width={96} height={96} alt="BuildEngine" style={{ display: "block", borderRadius: "22px" }} />
  );
}

/** The reused spinner ring (A2/A4). */
function SpinnerRing() {
  return (
    <span
      style={{ width: "44px", height: "44px", borderRadius: "50%", border: `3px solid ${BORDER}`, borderTopColor: AMBER, animation: "mark-spin .9s linear infinite" }}
    />
  );
}

export type LaunchVariant = "minimal" | "line" | "pitch";

/** A1 — Launch / Sign in. `reauth` reuses this screen with the "Sign back in"
 * button label (reference A1 note). */
export function AuthLaunch({
  onSignIn,
  variant = "line",
  reauth = false,
}: {
  onSignIn: () => void;
  variant?: LaunchVariant;
  reauth?: boolean;
}) {
  const line = variant === "pitch" ? "Talk. It gets written down." : "I manage tasks that kill your day";
  return (
    // #app has no height of its own, so rootStyle's 100% collapses to the content;
    // size to the small viewport (as app-shell does) so the screen fills the page.
    <div data-testid="auth-root" style={{ ...rootStyle, height: "100svh", gap: "20px" }}>
      {/* Equal 1fr rows either side keep the logo on the page's centre line, not just centred above the button. */}
      <div data-testid="auth-launch" style={{ flex: 1, display: "grid", gridTemplateRows: "1fr auto 1fr" }}>
        <div style={{ gridRow: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", textAlign: "center" }}>
          <BrandLogo />
          {variant !== "minimal" && (
            <p style={{ margin: 0, maxWidth: "260px", fontSize: "14px", lineHeight: 1.5, color: MUTED }}>{line}</p>
          )}
          {variant === "pitch" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px", textAlign: "left", marginTop: "8px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: AMBER, marginTop: "7px", flex: "none" }} />
                <span style={{ fontSize: "13.5px", lineHeight: 1.45, color: MUTED }}>Hold the button, talk like you would to a foreman.</span>
              </div>
            </div>
          )}
        </div>
        <div style={{ gridRow: 3, alignSelf: "end", display: "flex", flexDirection: "column", gap: "16px" }}>
          <PillButton size="hero" fullWidth data-testid="auth-signin" onClick={onSignIn}>
            {reauth ? "Sign back in" : "Sign in"}
          </PillButton>
          <p style={{ margin: 0, textAlign: "center", fontSize: "11.5px", lineHeight: 1.5, color: FAINT }}>
            We&rsquo;ll email you a sign-in link. No password to remember.
          </p>
        </div>
      </div>
    </div>
  );
}

/** A2 — Redirecting to AuthKit. */
export function AuthRedirecting() {
  return (
    <div data-testid="auth-root" style={rootStyle}>
      <div data-testid="auth-redirecting" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", textAlign: "center" }}>
        <SpinnerRing />
        <p style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: TEXT }}>Opening secure sign-in&hellip;</p>
        <p style={{ margin: 0, maxWidth: "250px", fontSize: "12.5px", lineHeight: 1.5, color: MUTED }}>
          Handing off to WorkOS AuthKit. You&rsquo;ll come right back here.
        </p>
      </div>
    </div>
  );
}

/** A4 — /callback (code→token + scope bootstrap). */
export function AuthCallback() {
  return (
    <div data-testid="auth-root" style={rootStyle}>
      <div data-testid="auth-callback" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", textAlign: "center" }}>
        <SpinnerRing />
        <p style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: TEXT }}>Signing you in&hellip;</p>
        <p style={{ margin: 0, maxWidth: "250px", fontSize: "12.5px", lineHeight: 1.5, color: MUTED }}>
          Setting up your projects. This only happens once on this phone.
        </p>
      </div>
    </div>
  );
}

const ERROR_BODIES: Record<AuthErrorKind, { title: string; body: string }> = {
  network: {
    title: "Can’t reach sign-in",
    body: "No signal, or our servers aren’t answering. You can keep capturing — everything queues on this phone and sends itself when you’re back.",
  },
  cancelled: {
    title: "Sign-in was cancelled",
    body: "You closed the sign-in page before it finished. No harm done — tap below to try again.",
  },
  denied: {
    title: "This account doesn’t have access",
    body: "Sign in through the right company. If this keeps happening, ask the person who set up your workspace to check your access.",
  },
  failed: {
    title: "Sign-in couldn’t be completed",
    body: "Your account could not be connected to this workspace. Check that you are signing in through the right company, then try again.",
  },
  expired: {
    title: "That code expired",
    body: "Codes only work for a short time. Send a new code and use the latest one that arrives.",
  },
  invalid: {
    title: "That code doesn’t match",
    body: "Check every digit, or send a new code. Only the latest code will work after a resend.",
  },
  superseded: {
    title: "A newer code was sent",
    body: "This code was replaced. Use the latest code in your inbox, or send a new one now.",
  },
  "rate-limited": {
    title: "Too many codes were requested",
    body: "Sign-in is temporarily paused to protect your account. WorkOS did not provide an exact reset time, so wait a few minutes before trying again.",
  },
};

/** A5 — Error. One screen, three bodies (kind). The queue-reassurance block +
 * the "keep capturing" secondary button appear only when queuedCount > 0. */
export function AuthError({
  kind,
  queuedCount,
  onRetry,
  onKeepCapturing,
}: {
  kind: AuthErrorKind;
  queuedCount: number;
  onRetry: () => void;
  onKeepCapturing?: () => void;
}) {
  const { title, body } = ERROR_BODIES[kind];
  const hasQueue = queuedCount > 0;
  return (
    <div data-testid="auth-root" style={rootStyle}>
      <div data-testid="auth-error" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "20px" }}>
        <span style={{ width: "52px", height: "52px", borderRadius: "50%", background: "var(--mark-status-critical-tint-14)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "24px", color: CRITICAL }}>!</span>
        <p style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: TEXT }}>{title}</p>
        <p style={{ margin: 0, fontSize: "13.5px", lineHeight: 1.55, color: MUTED }}>{body}</p>
        {hasQueue && (
          <div data-testid="auth-error-queue" style={{ borderRadius: "12px", background: WARN_TINT, boxShadow: `inset 0 0 0 1.5px ${AMBER}`, padding: "14px 16px" }}>
            <p style={{ margin: 0, fontSize: "12.5px", lineHeight: 1.5, color: TEXT }}>
              {queuedCount} {queuedCount === 1 ? "capture is" : "captures are"} still saved on this phone. Signing in is only needed to send {queuedCount === 1 ? "it" : "them"}.
            </p>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
          <PillButton fullWidth data-testid="auth-error-retry" onClick={onRetry}>
            {kind === "expired" || kind === "invalid" || kind === "superseded"
                ? "Send a new code"
                : kind === "rate-limited"
                  ? "Try sign-in again"
                : "Try again"}
          </PillButton>
          {hasQueue && (
            <button type="button" data-testid="auth-error-keep-capturing" onClick={onKeepCapturing} style={{ width: "100%", height: "48px", border: "none", borderRadius: "24px", background: "transparent", boxShadow: `inset 0 0 0 1px ${BORDER}`, color: TEXT, fontFamily: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
              Keep capturing offline
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A6 — No projects provisioned. Reached when auth succeeds but project_ids is
 * empty. Never falls through to a Record screen. */
export function AuthNoProjects({
  email,
  onCheckAgain,
  onSignOut,
}: {
  email: string;
  onCheckAgain: () => void;
  onSignOut: () => void;
}) {
  return (
    <div data-testid="auth-root" style={rootStyle}>
      <div data-testid="auth-no-projects" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "18px" }}>
        <p style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: TEXT }}>You&rsquo;re signed in, but no jobs yet</p>
        <p style={{ margin: 0, fontSize: "13.5px", lineHeight: 1.55, color: MUTED }}>
          Your account isn&rsquo;t assigned to a project. Whoever set up your company&rsquo;s account has to add you to a job before you can capture.
        </p>
        <p style={{ margin: 0, fontSize: "12.5px", color: FAINT }}>Signed in as {email}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
          <PillButton fullWidth data-testid="auth-no-projects-check" onClick={onCheckAgain}>
            Check again
          </PillButton>
          <button type="button" data-testid="auth-no-projects-signout" onClick={onSignOut} style={{ width: "100%", height: "48px", border: "none", borderRadius: "24px", background: "transparent", boxShadow: `inset 0 0 0 1px ${BORDER}`, color: MUTED, fontFamily: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export { SURFACE, AMBER, INK_ON_AMBER, WARN_TINT, TEXT, MUTED, FAINT, BORDER, CRITICAL, BTN_DISABLED };
